import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, Pencil, Ban, Eye } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { VoucherResponse } from '@/app/types/voucher';
import { DataTableColumnHeader } from '@/components/ui/data-table-column-header';

// Helper function to format currency
const formatCurrency = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR', // TODO: Make currency dynamic based on org settings
  }).format(Number(amount));
};

// Helper function to format date
const formatDate = (dateString: string | Date | undefined | null): string => {
  if (!dateString) return '-';
  try {
    // Assuming date string is YYYY-MM-DD from backend
    return format(new Date(dateString), 'dd/MM/yyyy');
  } catch (error) {
    return String(dateString); // Fallback
  }
};

// Type for action handlers passed to columns
interface PaymentsInActionHandlers {
    onView: (id: number) => void;
    // Add onEdit later if needed
}

export const paymentsInColumns = ({ onView }: PaymentsInActionHandlers): ColumnDef<VoucherResponse>[] => [
  {
    accessorKey: 'transaction_date',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
    cell: ({ row }) => <span>{formatDate(row.getValue('transaction_date'))}</span>,
    enableColumnFilter: true,
    meta: {
      label: 'Date',
      variant: 'dateRange',
      placeholder: 'Filter by date...',
    },
  },
  {
    accessorKey: 'voucher_number',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Payment #" />,
    cell: ({ row }) => <span className="font-medium">{row.getValue('voucher_number')}</span>,
    enableColumnFilter: true,
    meta: {
      label: 'Payment #',
      variant: 'text',
      placeholder: 'Search payment number...',
    },
  },
  {
    accessorKey: 'received_in_account_name',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Received In" />,
    cell: ({ row }) => <span>{row.getValue('received_in_account_name') || '-'}</span>,
    enableColumnFilter: true,
    meta: {
      label: 'Received In',
      variant: 'text',
      placeholder: 'Search account...',
    },
  },
  {
    id: 'party.name',
    accessorFn: (row) => row.party?.name || '',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Customer" />,
    cell: ({ row }) => <span>{row.original.party?.name || '-'}</span>, // Handle potential null party
    enableSorting: false, // Disable sorting on nested object property directly
    enableColumnFilter: true,
    meta: {
      label: 'Customer',
      variant: 'text',
      placeholder: 'Search customer name...',
    },
  },
  {
    accessorKey: 'total_amount',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />,
    cell: ({ row }) => <span className="font-medium text-right block">{formatCurrency(row.getValue('total_amount'))}</span>,
    enableColumnFilter: true,
    meta: {
      label: 'Amount',
      variant: 'number',
      placeholder: 'Filter amount...',
    },
  },
  {
    id: 'unallocated_amount',
    accessorFn: (row) => row.unallocated_amount || 0,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Unallocated" />,
    cell: ({ row }) => {
      const unallocated = row.original.unallocated_amount;
      const isCancelled = row.original.is_cancelled;
      
      // Don't show unallocated amount for cancelled vouchers
      if (isCancelled || !unallocated || Number(unallocated) === 0) {
        return <span className="text-muted-foreground text-right block">-</span>;
      }
      return <span className="font-medium text-right block text-orange-600">{formatCurrency(unallocated)}</span>;
    },
    enableSorting: true,
    enableColumnFilter: false,
  },
  {
    accessorKey: 'is_cancelled',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => (
      <Badge variant={row.getValue('is_cancelled') ? 'destructive' : 'secondary'}>
        {row.getValue('is_cancelled') ? 'Cancelled' : 'Active'}
      </Badge>
    ),
    enableColumnFilter: true,
    meta: {
      label: 'Status',
      variant: 'select',
      placeholder: 'All statuses',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Cancelled', value: 'cancelled' },
      ],
    },
    filterFn: (row, id, value) => { // Custom filter for boolean
        const isCancelled = row.getValue(id) === true;
        if (value === 'cancelled') return isCancelled;
        if (value === 'active') return !isCancelled;
        return true; // Show all if filter value is unexpected
    }
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      const paymentVoucher = row.original;
      const isCancelled = paymentVoucher.is_cancelled;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
           <DropdownMenuContent align="end">
              {/* View Action */}
              <DropdownMenuItem onClick={() => onView(paymentVoucher.id)}>
                <Eye className="mr-2 h-4 w-4" /> View Details
              </DropdownMenuItem>

              {!isCancelled && (
                <>
                    {/* Edit Action (Add later) */}
                    {/* <DropdownMenuItem onClick={() => onEdit(paymentVoucher.id)}>
                        <Pencil className="mr-2 h-4 w-4" /> Edit
                    </DropdownMenuItem> */}
                 </>
             )}
             {isCancelled && (
                 <DropdownMenuItem disabled>
                     <span className='opacity-50'>Cancelled</span>
                 </DropdownMenuItem>
             )}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];